import axios, { AxiosError, AxiosResponse } from 'axios'
import { format } from 'date-fns'
import mixpanel from 'mixpanel-browser'
import MixPanelEventos from '../../consts/mixpanel/eventos'
import MixPanelPropriedades from '../../consts/mixpanel/propriedades'
import mixpanelConfig from '../../consts/mixpanel/token'
import {
  MixPanelAbrirAtendimentoViaChat,
  MixPanelConsultarPrecoConsulta,
  MixPanelEscolhaLocal,
  MixPanelEscolhaMedicoEspecialidade,
  MixPanelEscolherFormaDePagamento,
  MixPanelEscolherHorarios,
  MixPanelExibirErroElegibilidade,
  MixPanelFiltrarHorarios,
  MixPanelFluxoDadosRelevantes,
  MixPanelInformarDadosPessoais,
  MixPanelFinalizarCadastro,
  MixPanelCancelarAgendamento,
  TrackData,
  MixPanelType
} from '../../models/MixPanel'
import { store } from '../../store'
import { ValidacaoElegibilidade } from './elegibilidadeHelper'
import { obtemStepAtual } from './stepbarHelpers'
import {
  obtemUnidadesValidacao,
  obtemUnidadesPorCobertura
} from './unidadeHelper'

class MixpanelHelper {
  private static _token = mixpanelConfig.token

  private static _dateTimeFormat = 'yyyy-MM-dd HH:mm:ss'

  static init() {
    mixpanel.init(MixpanelHelper._token)
  }

  static trackEvent(eventName: string, data?: TrackData) {
    const commomInformation = {
      [MixPanelPropriedades.APLICACAO_NOME]: 'Consultas',
      [MixPanelPropriedades.APLICACAO_VERSAO]: 'v3'
    }

    if (data) {
      const dataToSend = {
        ...this.replaceKeysFromEventObject(data as Record<string, unknown>),
        ...commomInformation
      }
      mixpanel.track(eventName, dataToSend)

      return
    }
    mixpanel.track(eventName, commomInformation)
  }

  static reset() {
    mixpanel.reset()
  }

  private static replaceKeysFromEventObject(
    eventObject: Record<string, unknown>
  ) {
    const objectWithKeysReplaced: Record<string, unknown> = {}

    Object.keys(eventObject).forEach(key => {
      objectWithKeysReplaced[
        (MixPanelPropriedades as Record<string, string>)[key]
      ] = eventObject[key]
    })

    return objectWithKeysReplaced
  }

  private static getStringValueOrUndefined(
    property?: number
  ): string | undefined {
    if (property) {
      return String(property)
    }

    return undefined
  }

  private static buildDataFluxoDadosRelevantes(): MixPanelFluxoDadosRelevantes {
    const {
      escolhaPorMedico,
      medico,
      especialidade,
      agendamentoTeleconsulta,
      local,
      pagamento,
      paciente,
      unidade,
      numeroRequisicao
    } = store.getState().consulta

    const eventData: MixPanelFluxoDadosRelevantes = {
      MODALIDADE: agendamentoTeleconsulta ? 'TELECONSULTA' : 'PRESENCIAL',
      FLUXO_DE_ATENDIMENTO: escolhaPorMedico
        ? 'MÉDICO ESPECÍFICO'
        : 'ESPECIALIDADE',
      MEDICO_CODIGO: this.getStringValueOrUndefined(medico?.id),
      MEDICO_NOME: medico?.nome,
      ESPECIALIDADE_CODIGO: especialidade?.id,
      ESPECIALIDADE_DESCRICAO: especialidade?.descricao,
      LOCAL_UF: local?.uf,
      LOCAL_CODIGO: this.getStringValueOrUndefined(local?.id),
      LOCAL_DESCRICAO: local?.nome,
      LOCAL_TIPO: local?.tipo,
      PACIENTE_DATA_NASCIMENTO: paciente?.dataNascimento,
      PACIENTE_GENERO: paciente?.genero,
      FORMA_DE_PAGAMENTO: pagamento
        ? pagamento.particular
          ? 'PARTICULAR'
          : 'PLANO'
        : undefined,
      CONVENIO_CODIGO:
        pagamento && pagamento.convenio
          ? String(pagamento?.convenio?.idMotion)
          : undefined,
      CONVENIO_NOME: pagamento?.convenio?.nome,
      PLANO_CODIGO:
        pagamento && pagamento.plano
          ? String(pagamento?.plano?.idMotion)
          : undefined,
      PLANO_NOME: pagamento?.plano?.nome,
      UNIDADE_NOME: unidade?.nome,
      UNIDADE_CODIGO: String(unidade?.id),
      UNIDADE_SETOR: unidade?.setorId,
      NUMERO_REQUISICAO: numeroRequisicao
    }

    return eventData
  }

  private static buildDataEventEscolhaMedicoEspecialidade(): MixPanelEscolhaMedicoEspecialidade {
    const { medico, especialidade } = store.getState().consulta

    const eventData: MixPanelEscolhaMedicoEspecialidade = {
      ...this.buildDataFluxoDadosRelevantes(),
      MEDICO_CRM: medico?.crm,
      MEDICO_CODIGO_RDSL: medico?.idRdsl ? String(medico?.idRdsl) : undefined,
      MEDICO_GENERO: medico?.genero,
      ESPECIALIDADE_POSSUI_CONSULTA_DIGITAL: especialidade?.isConsultaDigital,
      ESPECIALIDADE_POSSUI_ATENDIMENTO_ONLINE:
        especialidade?.possuiAgendamentoOnline
    }

    return eventData
  }

  static formataDataHora(data: Date) {
    return format(data, this._dateTimeFormat)
  }

  static sendEventEscolhaMedicoEspecialidade(
    eventCustomData?: MixPanelEscolhaMedicoEspecialidade
  ): void {
    const eventData: MixPanelEscolhaMedicoEspecialidade = {
      ...this.buildDataEventEscolhaMedicoEspecialidade(),
      ...(eventCustomData || {})
    }

    MixpanelHelper.trackEvent(
      MixPanelEventos.ESCOLHA_MEDICO_ESPECIALIDADE,
      eventData
    )
  }

  private static buildDataEventEscolhaLocal(): MixPanelEscolhaLocal {
    const { local } = store.getState().consulta

    const eventData: MixPanelEscolhaLocal = {
      ...this.buildDataFluxoDadosRelevantes(),
      LOCAL_CODIGO_RDSL: this.getStringValueOrUndefined(local?.idRdsl)
    }

    return eventData
  }

  static sendEventEscolhaLocal(eventCustomData?: MixPanelEscolhaLocal): void {
    const eventData: MixPanelEscolhaLocal = {
      ...this.buildDataEventEscolhaLocal(),
      ...(eventCustomData || {})
    }

    MixpanelHelper.trackEvent(MixPanelEventos.ESCOLHA_LOCAL, eventData)
  }

  private static buildDataEventInformarDadosPessoais(): MixPanelInformarDadosPessoais {
    const eventData: MixPanelInformarDadosPessoais = {
      ...this.buildDataFluxoDadosRelevantes()
    }

    return eventData
  }

  static sendEventInformarDadosPessoais(
    eventCustomData?: MixPanelInformarDadosPessoais
  ): void {
    const eventData: MixPanelInformarDadosPessoais = {
      ...this.buildDataEventInformarDadosPessoais(),
      ...(eventCustomData || {})
    }

    MixpanelHelper.trackEvent(
      MixPanelEventos.INFORMAR_DADOS_PESSOAIS,
      eventData
    )
  }

  private static buildDataEventEscolherFormaDePagamento(): MixPanelEscolherFormaDePagamento {
    const eventData: MixPanelEscolherFormaDePagamento = {
      ...this.buildDataFluxoDadosRelevantes()
    }

    return eventData
  }

  static sendEventEscolherFormaDePagamento(
    eventCustomData?: MixPanelEscolherFormaDePagamento
  ): void {
    const eventData: MixPanelEscolherFormaDePagamento = {
      ...this.buildDataEventEscolherFormaDePagamento(),
      ...(eventCustomData || {})
    }

    MixpanelHelper.trackEvent(
      MixPanelEventos.ESCOLHER_FORMA_PAGAMENTO,
      eventData
    )
  }

  private static buildDataEventExibirErroElegibilidade(): MixPanelExibirErroElegibilidade {
    const eventData: MixPanelExibirErroElegibilidade = {
      ...this.buildDataFluxoDadosRelevantes(),
      FALLBACK_EXIBIDO: true
    }

    return eventData
  }

  static sendEventExibirErroElegibilidade(
    eventCustomData?: MixPanelExibirErroElegibilidade
  ): void {
    const eventData: MixPanelExibirErroElegibilidade = {
      ...this.buildDataEventExibirErroElegibilidade(),
      ...(eventCustomData || {})
    }

    MixpanelHelper.trackEvent(
      MixPanelEventos.EXIBIR_ERRO_ELEGIBILIDADE,
      eventData
    )
  }

  private static buildDataEventConsultarPrecoConsulta(): MixPanelConsultarPrecoConsulta {
    const eventData: MixPanelConsultarPrecoConsulta = {
      ...this.buildDataFluxoDadosRelevantes()
    }

    return eventData
  }

  static sendEventConsultarPrecoConsulta(
    eventCustomData?: MixPanelConsultarPrecoConsulta
  ): void {
    const eventData: MixPanelConsultarPrecoConsulta = {
      ...this.buildDataEventConsultarPrecoConsulta(),
      ...(eventCustomData || {})
    }

    MixpanelHelper.trackEvent(
      MixPanelEventos.CONSULTAR_PRECO_CONSULTA,
      eventData
    )
  }

  private static buildDataEventFiltrarHorarios(): MixPanelFiltrarHorarios {
    const eventData: MixPanelFiltrarHorarios = {
      ...this.buildDataFluxoDadosRelevantes()
    }

    return eventData
  }

  static sendEventFiltrarHorarios(
    eventCustomData?: MixPanelFiltrarHorarios
  ): void {
    const eventData: MixPanelFiltrarHorarios = {
      ...this.buildDataEventFiltrarHorarios(),
      ...(eventCustomData || {})
    }

    MixpanelHelper.trackEvent(MixPanelEventos.FILTRAR_HORARIOS, eventData)
  }

  private static buildDataEventAbrirAtendimentoViaChat(): MixPanelAbrirAtendimentoViaChat {
    const eventData: MixPanelAbrirAtendimentoViaChat = {
      ...this.buildDataFluxoDadosRelevantes()
    }

    return eventData
  }

  static sendEventAbrirAtendimentoViaChat(
    eventCustomData?: MixPanelAbrirAtendimentoViaChat
  ): void {
    const eventData: MixPanelAbrirAtendimentoViaChat = {
      ...this.buildDataEventAbrirAtendimentoViaChat(),
      ...(eventCustomData || {})
    }

    MixpanelHelper.trackEvent(MixPanelEventos.ABRIR_ATENDIMENTO_CHAT, eventData)
  }

  private static buildDataEventEscolherHorarios(): MixPanelEscolherHorarios {
    const eventData: MixPanelEscolherHorarios = {
      ...this.buildDataFluxoDadosRelevantes()
    }

    return eventData
  }

  static sendEventEscolherHorarios(
    eventCustomData?: MixPanelEscolherHorarios
  ): void {
    const eventData: MixPanelEscolherHorarios = {
      ...this.buildDataEventEscolherHorarios(),
      ...(eventCustomData || {})
    }

    MixpanelHelper.trackEvent(MixPanelEventos.ESCOLHER_HORARIOS, eventData)
  }

  private static buildDataEventFinalizarCadastro(): MixPanelFinalizarCadastro {
    const eventData: MixPanelFinalizarCadastro = {
      ...this.buildDataFluxoDadosRelevantes()
    }

    return eventData
  }

  static sendEventFinalizarCadastro(
    eventCustomData?: MixPanelFinalizarCadastro
  ): void {
    const eventData: MixPanelFinalizarCadastro = {
      ...this.buildDataEventFinalizarCadastro(),
      ...(eventCustomData || {})
    }

    MixpanelHelper.trackEvent(MixPanelEventos.FINALIZAR_CADASTRO, eventData)
  }

  private static buildDataEventAcessarEspera(): MixPanelType.AcessarEspera {
    const eventData: MixPanelType.AcessarEspera = {
      ...this.buildDataFluxoDadosRelevantes()
    }

    return eventData
  }

  static sendEventAcessarEspera(
    eventCustomData?: MixPanelType.AcessarEspera
  ): void {
    const eventData: MixPanelType.AcessarEspera = {
      ...this.buildDataEventAcessarEspera(),
      ...(eventCustomData || {})
    }

    MixpanelHelper.trackEvent(MixPanelEventos.ACESSAR_ESPERA, eventData)
  }

  static sendEventCancelarConsulta(
    eventCustomData: MixPanelCancelarAgendamento
  ) {
    MixpanelHelper.trackEvent(
      MixPanelEventos.CANCELAR_AGENDAMENTO,
      eventCustomData
    )
  }

  private static buildDataEventAcessarTeleconsulta(): MixPanelType.AcessarTeleconsulta {
    const eventData: MixPanelType.AcessarTeleconsulta = {
      ...this.buildDataFluxoDadosRelevantes()
    }

    return eventData
  }

  static sendEventAcessarTeleconsulta(
    eventCustomData?: MixPanelType.AcessarTeleconsulta
  ): void {
    const eventData: MixPanelType.AcessarTeleconsulta = {
      ...this.buildDataEventAcessarTeleconsulta(),
      ...(eventCustomData || {})
    }

    MixpanelHelper.trackEvent(MixPanelEventos.ACESSAR_TELECONSULTA, eventData)
  }

  static sendEventListarHorarios(
    eventCustomData?: MixPanelType.ListarHorarios
  ): void {
    const eventData: MixPanelType.AcessarTeleconsulta = {
      ...this.buildDataFluxoDadosRelevantes(),
      ...(eventCustomData || {})
    }

    MixpanelHelper.trackEvent(MixPanelEventos.LISTAR_HORARIOS, eventData)
  }

  static sendEventVerificarCobertura(
    validacoes: ValidacaoElegibilidade[] = []
  ): void {
    const stepAtual = obtemStepAtual()

    if (stepAtual?.name !== 'pagamento') return

    const unidades = obtemUnidadesValidacao(validacoes)

    const eventData: MixPanelType.AcessarTeleconsulta = {
      ...this.buildDataFluxoDadosRelevantes(),
      ...obtemUnidadesPorCobertura(unidades)
    }

    MixpanelHelper.trackEvent(MixPanelEventos.VERIFICAR_COBERTURA, eventData)
  }

  static sendEventExibirErros(
    paginaOrigem: string,
    erro: Error | AxiosError
  ): void {
    if (axios.isAxiosError(erro)) {
      const eventData: MixPanelType.ExibirErro = {
        ...this.buildDataFluxoDadosRelevantes(),
        TIPO_ERRO: erro.response?.status,
        MENSAGEM_ERRO: erro.message,
        PAGINA_ORIGEM_ERRO: paginaOrigem,
        ENDERECO_REQUISICAO: erro.request.responseURL
      }

      MixpanelHelper.trackEvent(MixPanelEventos.EXIBIR_ERRO, eventData)
    }
  }

  static sendEventExibirErrosGenericos(
    paginaOrigem: string,
    erro: AxiosResponse
  ): void {
    const eventData: MixPanelType.ExibirErroGenerico = {
      ...this.buildDataFluxoDadosRelevantes(),
      METODO_REQUISICAO: erro.config?.method,
      CODIGO_ERRO_BACKEND: erro.data?.code,
      MENSAGEM_ERRO_BACKEND: erro.data?.message,
      STATUS_ERRO_RETORNO_REQUISICAO: erro.status,
      STATUS_TEXTO_ERRO_RETORNO_REQUISICAO: erro.statusText,
      PARAMENTROS_REQUISICAO: erro.config?.params,
      PAGINA_ORIGEM_ERRO: paginaOrigem,
      ENDERECO_REQUISICAO: erro.config?.url
    }

    MixpanelHelper.trackEvent(
      MixPanelEventos.ARMAZENAR_ERRO_GENERICO,
      eventData
    )
  }

  static sendEventPermitirGeoLocalizacao(localizacaoPermtida: boolean): void {
    const eventData: MixPanelType.PermitirGeoLocalizacao = {
      ...this.buildDataFluxoDadosRelevantes(),
      GEO_LOCALIZACAO_FOI_PERMITIDA: localizacaoPermtida
    }

    MixpanelHelper.trackEvent(
      MixPanelEventos.PERMITIR_GEO_LOCALIZACAO,
      eventData
    )
  }

  static sendEventBloquearGeoLocalizacao(localizacaoBloqueada: boolean): void {
    const eventData: MixPanelType.BloquearGeoLocalizacao = {
      ...this.buildDataFluxoDadosRelevantes(),
      GEO_LOCALIZACAO_FOI_BLOQUEADA: localizacaoBloqueada
    }
    MixpanelHelper.trackEvent(
      MixPanelEventos.BLOQUEAR_GEO_LOCALIZACAO,
      eventData
    )
  }

  static sendEventToggleGeoLocalizacaoFoiAtivado(toggleAtivado: boolean): void {
    const eventData: MixPanelType.ToggleGeoLocalizacaoFoiAtivado = {
      ...this.buildDataFluxoDadosRelevantes(),
      TOGGLE_GEO_LOCALIZACAO_FOI_ATIVADO: toggleAtivado
    }

    MixpanelHelper.trackEvent(
      MixPanelEventos.ATIVAR_TOGGLE_GEO_LOCALIZACAO,
      eventData
    )
  }

  static sendEventToggleGeoLocalizacaoFoiDesativado(
    toggleDesativado: boolean
  ): void {
    const eventData: MixPanelType.ToggleGeoLocalizacaoFoiDesativada = {
      ...this.buildDataFluxoDadosRelevantes(),
      TOGGLE_GEO_LOCALIZACAO_FOI_DESATIVADO: toggleDesativado
    }

    MixpanelHelper.trackEvent(
      MixPanelEventos.DESATIVAR_TOGGLE_GEO_LOCALIZACAO,
      eventData
    )
  }

  static sendEventMostrarModalDeGeolocalizacao(modalExibido: boolean): void {
    const eventData: MixPanelType.ModaleGeoLocalizacaoFoiEibido = {
      ...this.buildDataFluxoDadosRelevantes(),
      GEO_LOCALIZACAO_MODAL_FOI_EXIBIDA: modalExibido
    }

    MixpanelHelper.trackEvent(
      MixPanelEventos.GEO_LOCALIZACAO_MODAL_FOI_EXIBIDO,
      eventData
    )
  }

  static sendEventClicouNaSugestaoDeLocal(clicouNaSugestao: boolean): void {
    const eventData: MixPanelType.ClicouNaSugestaoDeLocal = {
      ...this.buildDataFluxoDadosRelevantes(),
      CLICOU_NA_SUGESTAO_DE_LOCAL: clicouNaSugestao
    }

    MixpanelHelper.trackEvent(
      MixPanelEventos.CLICOU_NA_SUGESTAO_LOCAL,
      eventData
    )
  }

  static sendEventAdicionarAgenda(servicoSelecionado = ''): void {
    if (servicoSelecionado === '') return

    const eventData: MixPanelType.AdicionarAgenda = {
      ...this.buildDataFluxoDadosRelevantes(),
      SERVICO_SELECIONADO: servicoSelecionado
    }

    MixpanelHelper.trackEvent(MixPanelEventos.ADICIONAR_AGENDA, eventData)
  }
}

export default MixpanelHelper
